import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Bell, Star } from "lucide-react";
import { StudentShell } from "../components/StudentShell";
import { MascotGuide } from "../components/MascotGuide";
import { useLang } from "../i18n";
import { api, useAuth } from "../api";
import { ASSETS } from "../assets";
import { gradeProfile } from "../gradeProfile";
import { sfx } from "../sfx";

const TILES = [
  { to: "/student/journey", key: "journey", label: "الخريطة", icon: ASSETS.games, from: "#ff6b6b", to2: "#c02b3d" },
  { to: "/student/chat", key: "chat", label: "حوار AI", icon: ASSETS.tutor, from: "#2ec4b6", to2: "#12776f" },
  { to: "/student/quiz", key: "quiz", icon: ASSETS.quiz, from: "#8b5cf6", to2: "#5b21b6" },
  { to: "/student/tasks", key: "myTasks", icon: ASSETS.tasks, from: "#ff9f1c", to2: "#c76a00" },
  { to: "/student/practice", key: "writing", icon: ASSETS.tasks, from: "#2f7bff", to2: "#1746a8" },
  { to: "/student/dictionary", key: "dictionary", icon: ASSETS.dictionary, from: "#3fa9ff", to2: "#1a63b8" },
  { to: "/student/competitions", key: "competitions", icon: ASSETS.progress, from: "#f0b429", to2: "#b46b00" },
  { to: "/student/leaderboard", key: "leaderboard", icon: ASSETS.progress, from: "#3ec46d", to2: "#177a41" },
];

export default function Student() {
  const { t } = useLang();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [weak, setWeak] = useState([]);
  const profile = gradeProfile(user?.grade);

  useEffect(() => {
    api.get("/student/dashboard").then((r) => {
      setD(r.data);
      const seen = JSON.parse(localStorage.getItem("el_badges") || "[]");
      const fresh = (r.data.badges || []).filter((b) => !seen.includes(b));
      if (fresh.length) {
        sfx.badge();
        localStorage.setItem("el_badges", JSON.stringify(r.data.badges || []));
      }
    });
    api.get("/weak-words").then((r) => setWeak(r.data.words)).catch(() => {});
  }, []);

  const pct = Math.min(100, Math.round((d?.points ?? 0) / 20));

  return (
    <StudentShell right={
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 rounded-full px-4 py-2 font-extrabold text-sm text-[#05122e]"
          style={{ background: "linear-gradient(180deg,#ffd166,#ff9f1c)", boxShadow: "0 5px 0 rgba(4,12,32,.4)" }}
          data-testid="points-badge">
          <Star size={15} />{d?.points ?? 0}
        </div>
        <Link to="/student/tasks" data-testid="notif-btn"
          className="relative w-10 h-10 rounded-full grid place-items-center bg-white/10 text-white border border-white/15">
          <Bell size={16} />
          {!!d?.assignments_pending && (
            <span className="absolute -top-1 -end-1 w-5 h-5 rounded-full text-[10px] font-black grid place-items-center text-white"
              style={{ background: "var(--play-accent)" }}>{d.assignments_pending}</span>
          )}
        </Link>
      </div>}>

      {/* ---------- Hero ---------- */}
      <motion.section initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }}
        className="glass relative overflow-hidden p-6 sm:p-10" data-testid="student-hero">
        <div className="absolute inset-0 opacity-90 pointer-events-none"
          style={{ background: "radial-gradient(700px 320px at 85% 10%, rgba(63,169,255,.35), transparent 65%)" }} />
        <div className="relative flex flex-col lg:flex-row items-center gap-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-4xl sm:text-5xl font-black text-white glow-text">
              {t("welcome")} {user?.full_name} <span className="inline-block">👋</span>
            </h1>
            <p className="text-white/70 mt-3 text-base">
              {t("grade")} {d?.grade ?? user?.grade}{d?.class_name ? ` · ${d.class_name}` : ""} · {user?.school_name}
            </p>

            <div className="mt-7 max-w-lg">
              <div className="flex justify-between text-xs font-bold text-white/70">
                <span>{t("progress")}</span><span>{pct}%</span>
              </div>
              <div className="mt-2 h-4 rounded-full bg-white/10 overflow-hidden border border-white/15">
                <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1.1 }}
                  className="h-full rounded-full"
                  style={{ background: "linear-gradient(90deg,#3fa9ff,#2ec4b6)" }} />
              </div>
              <div className="grid grid-cols-3 gap-3 mt-6">
                {[[d?.points ?? 0, t("points"), "#ffd166"],
                  [d?.games_played ?? 0, t("gamesPlayed"), "#2ec4b6"],
                  [d?.quizzes?.length ?? 0, t("quizzesTaken"), "#ff8fa3"]].map(([v, l, c]) => (
                  <div key={l} className="glass-soft py-3 text-center">
                    <div className="text-2xl font-black" style={{ color: c }}>{v}</div>
                    <div className="text-[11px] font-bold text-white/60">{l}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-4 flex-wrap" data-testid="badges-list">
                {(d?.badges || []).map((b) => (
                  <span key={b} className="rounded-full px-3 py-1 text-xs font-extrabold text-[#05122e]"
                    style={{ background: "linear-gradient(180deg,#ffe08a,#ffb02e)" }}>🏅 {b}</span>
                ))}
              </div>
            </div>
          </div>

          <img src={ASSETS.island} alt="English Lab" data-testid="hero-island"
            className="w-full max-w-md float-slow mix-blend-screen soft-mask-wide" />
        </div>
      </motion.section>

      {!!weak.length && (
        <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
          className="glass-soft p-5 mt-6" data-testid="weak-words-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base md:text-lg font-black text-white">كلماتي الصعبة 🎯</h2>
            <Link to="/student/quiz" data-testid="weak-practice-link"
              className="play-btn text-white text-xs" style={{ background: "var(--play-primary)" }}>
              تدرّب عليها الآن</Link>
          </div>
          <p className="text-xs text-white/55 mt-1">
            تظهر في كويزاتك وألعابك أكثر، وتُشطب بعد إجابتين صحيحتين متتاليتين.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {weak.slice(0, 14).map((w) => (
              <span key={w.word} data-testid={`weak-word-${w.word}`} dir="ltr"
                className="rounded-full px-3 py-1 text-xs font-bold"
                style={{ background: "rgba(255,93,93,.18)", color: "#ffb0b0" }}>
                {w.word} · ×{w.misses}</span>
            ))}
          </div>
        </motion.section>
      )}

      {/* ---------- Tiles ---------- */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 mt-8">
        {TILES.filter((tile) => !(profile.tier === "young" && ["competitions", "leaderboard"].includes(tile.key)))
          .map((tile, i) => (
          <motion.div key={tile.key} initial={{ opacity: 0, y: 26, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", stiffness: 240, damping: 20, delay: i * 0.06 }}>
            <Link to={tile.to} data-testid={`tile-${tile.key}`} onClick={() => sfx.click()}
              className="tile flex flex-col items-center justify-center gap-3 h-44"
              style={{ background: `linear-gradient(160deg,${tile.from},${tile.to2})` }}>
              <img src={tile.icon} alt="" className="relative z-10 w-20 h-20 object-contain mix-blend-screen
                drop-shadow-[0_6px_14px_rgba(0,0,0,.5)]" />
              <span className={`relative z-10 font-extrabold ${profile.tier === "young" ? "text-xl" : "text-lg"}
                text-center leading-tight drop-shadow-[0_2px_6px_rgba(0,0,0,.45)]`}>{tile.label || t(tile.key)}</span>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* ---------- Mascot guide ---------- */}
      <div className="mt-8">
        <MascotGuide grade={user?.grade} name={user?.full_name} />
      </div>

      {/* ---------- Mascot strip ---------- */}
      <div className="glass mt-8 p-6 flex items-center gap-6 overflow-hidden">
        <img src={ASSETS.mascot} alt="" className="w-24 sm:w-32 float-slow mix-blend-screen soft-mask" />
        <div>
          <h3 className="text-xl sm:text-2xl font-black text-white">
            {d?.assignments_pending ? `${t("newTasks")}: ${d.assignments_pending}` : `${profile.book}`}
          </h3>
          <p className="text-white/65 text-sm mt-1">{d?.topics_count ?? 0} {t("topics")} · {t("grade")} {d?.grade ?? user?.grade}</p>
          <Link to={d?.assignments_pending ? "/student/tasks" : "/student/journey"} data-testid="hero-cta"
            className="btn3d inline-block mt-4 text-sm"
            style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}>
            {d?.assignments_pending ? t("myTasks") : "تابع رحلتك"}
          </Link>
        </div>
      </div>
    </StudentShell>
  );
}
